// file: client/src/offline/idb.js
/**
 * MODULE 09 — minimal promise wrapper over IndexedDB (no dependency).
 * One database, fixed store set, versioned migrations kept trivial.
 */
const DB_NAME = "pharmakon_offline";
const DB_VERSION = 2;

/** Object stores: reference caches + the outbox + durable drafts + meta. */
const STORES = [
  { name: "ref_produits", key: "id" },
  { name: "ref_stock", key: "id" },
  { name: "ref_clients", key: "id" },
  { name: "ref_medecins", key: "id" },
  // Offline sale drafts (create→validate is two calls): durable so a refresh
  // mid-sale offline never loses the basket (the in-memory Map was the bug).
  { name: "offline_drafts", key: "id" },
  { name: "outbox", key: "id" },
  { name: "meta", key: "k" },
];

let dbPromise = null;

export function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const s of STORES) {
        if (!db.objectStoreNames.contains(s.name)) {
          const st = db.createObjectStore(s.name, { keyPath: s.key });
          if (s.name === "outbox") st.createIndex("statut", "statut");
          if (s.name === "ref_produits") st.createIndex("nom", "nom_commercial");
          if (s.name === "ref_stock") st.createIndex("produit", "produit_id");
        }
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

const tx = (db, store, mode) => db.transaction(store, mode).objectStore(store);
const wrap = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

export async function idbPut(store, value) {
  const db = await openDb();
  return wrap(tx(db, store, "readwrite").put(value));
}

export async function idbBulkPut(store, values) {
  if (!values.length) return 0;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, "readwrite");
    const st = t.objectStore(store);
    for (const v of values) st.put(v);
    t.oncomplete = () => resolve(values.length);
    t.onerror = () => reject(t.error);
  });
}

export async function idbGet(store, key) {
  const db = await openDb();
  return wrap(tx(db, store, "readonly").get(key));
}

export async function idbAll(store) {
  const db = await openDb();
  return wrap(tx(db, store, "readonly").getAll());
}

export async function idbDelete(store, key) {
  const db = await openDb();
  return wrap(tx(db, store, "readwrite").delete(key));
}

export async function idbCount(store) {
  const db = await openDb();
  return wrap(tx(db, store, "readonly").count());
}

export async function idbClear(store) {
  const db = await openDb();
  return wrap(tx(db, store, "readwrite").clear());
}

export const metaGet = async (k) => (await idbGet("meta", k))?.v ?? null;
export const metaSet = (k, v) => idbPut("meta", { k, v });
